(() => {
  const STORAGE_PREFIX = 'lcs-favorites';
  const EVENT_CHANGE = 'lcs:favorites:change';
  const EVENT_UPDATED = 'lcs:favorites-updated';

  const getCustomerId = () => {
    const id = document.documentElement.dataset.customerId || document.body.dataset.customerId || window.LCS_CUSTOMER_ID || 'guest';
    return String(id || 'guest');
  };

  const isLoggedIn = () => {
    return document.documentElement.dataset.customerLoggedIn === 'true' || document.body.dataset.customerLoggedIn === 'true' || window.LCS_CUSTOMER_LOGGED_IN === true;
  };

  const getStorageKey = () => `${STORAGE_PREFIX}-${getCustomerId() || 'guest'}`;

  const normalizeOptions = (options) => {
    if (!Array.isArray(options)) return [];
    return options
      .map((option) => {
        if (typeof option === 'string') return { name: '', value: option };
        if (!option) return null;
        return { name: option.name || '', value: option.value || '' };
      })
      .filter((option) => option && option.value);
  };

  const normalizeItem = (item = {}) => {
    const options = normalizeOptions(item.options);
    const colorOption = options.find((option) => /colou?r/i.test(option.name));
    const sizeOption = options.find((option) => /size/i.test(option.name));
    const productId = String(item.productId || '');
    const variantId = String(item.variantId || '');
    const key = item.key || `${productId || item.handle || 'product'}:${variantId || 'variant'}`;

    return {
      key,
      productId,
      variantId,
      handle: item.handle || '',
      title: item.title || 'Favorite item',
      url: item.url || (item.handle ? `/products/${item.handle}` : '#'),
      image: item.image || '',
      price: item.price ?? null,
      priceText: item.priceText || item.price_text || item.price || '',
      compareAtPrice: item.compareAtPrice || '',
      options,
      optionSummary: options.map((option) => option.name ? `${option.name}: ${option.value}` : option.value).join(' / '),
      color: item.color || (colorOption ? colorOption.value : ''),
      size: item.size || (sizeOption ? sizeOption.value : ''),
      quantity: Math.max(1, Number(item.quantity || 1)),
      available: item.available !== false,
      savedAt: item.savedAt || item.addedAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  };

  const read = () => {
    try {
      const raw = localStorage.getItem(getStorageKey());
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(Boolean).map(normalizeItem);
    } catch (error) {
      return [];
    }
  };

  const broadcast = (items) => {
    const detail = { items, count: items.length, storageKey: getStorageKey() };
    window.dispatchEvent(new CustomEvent(EVENT_CHANGE, { detail }));
    window.dispatchEvent(new CustomEvent(EVENT_UPDATED, { detail }));
  };

  const write = (items) => {
    const deduped = [];
    const seen = new Set();
    (Array.isArray(items) ? items : []).forEach((item) => {
      const normalized = normalizeItem(item);
      if (!normalized.key || seen.has(normalized.key)) return;
      seen.add(normalized.key);
      deduped.push(normalized);
    });
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(deduped));
    } catch (error) {
      // Storage can fail in private browsing. The storefront should not break.
    }
    broadcast(deduped);
    return deduped;
  };

  const has = (key) => read().some((item) => item.key === key);

  const add = (item) => {
    const normalized = normalizeItem(item);
    const items = read().filter((favorite) => favorite.key !== normalized.key);
    items.unshift(normalized);
    return write(items);
  };

  const remove = (key) => write(read().filter((item) => item.key !== key));

  const removeMany = (keys) => {
    const keySet = new Set(keys);
    const current = read();
    const removed = current.filter((item) => keySet.has(item.key));
    write(current.filter((item) => !keySet.has(item.key)));
    return removed;
  };

  const requireLoginOrContinue = () => {
    if (isLoggedIn()) return true;
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/account/login?return_url=${returnUrl}`;
    return false;
  };

  const updateHeaderCount = () => {
    const count = read().length;
    document.querySelectorAll('[data-header-favorites-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count < 1;
      badge.setAttribute('aria-hidden', count < 1 ? 'true' : 'false');
    });
    document.querySelectorAll('[data-header-favorites-link]').forEach((link) => {
      link.classList.toggle('has-favorites', count > 0);
      link.setAttribute('aria-label', count > 0 ? `Favorites, ${count} saved` : 'Favorites');
    });
  };

  const addItemsToCart = async (items) => {
    const cartItems = (Array.isArray(items) ? items : [])
      .filter((item) => item && item.variantId)
      .map((item) => ({ id: Number(item.variantId), quantity: Math.max(1, Number(item.quantity || 1)) }));

    if (!cartItems.length) throw new Error('Select at least one available favorite.');

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: cartItems })
    });

    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.status) throw new Error(body.message || 'Unable to add selected favorites.');

    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'favorites' } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'favorites' } }));
    return body;
  };

  window.LCSFavorites = { EVENT_CHANGE, EVENT_UPDATED, getStorageKey, isLoggedIn, requireLoginOrContinue, normalizeItem, read, write, has, add, remove, removeMany, updateHeaderCount, addItemsToCart };

  document.addEventListener('DOMContentLoaded', updateHeaderCount);
  window.addEventListener(EVENT_CHANGE, updateHeaderCount);
  window.addEventListener(EVENT_UPDATED, updateHeaderCount);
  window.addEventListener('storage', (event) => {
    if (event.key === getStorageKey()) {
      updateHeaderCount();
      broadcast(read());
    }
  });
})();
